import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Divider,
  FormHelperText,
  Grid,
  TextField
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { DataGrid } from '@material-ui/data-grid';

import 'react-dates/initialize';
import { DateRangePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';

import AddProductModal from 'components/PurchaseOrders/AddProductModal';
import AddVendorModal from 'components/PurchaseOrders/AddVendorModal';

import { addPurchaseOrder, setNewPOVendorId, setNewPOProducts, setNewPODate, clearNewPO } from 'redux/actions';
import { VAT_UAE } from 'variables/globalVAT';

const mapStateToProps = (state) => ({
  newPurchaseOrder: state.newPurchaseOrder
});

const filter = createFilterOptions();

const CustomFooter = (props) => (
  <React.Fragment>
    {props.selectionModel.length > 0 ?
      <Grid item style={{padding: 8, display: 'flex'}}>
        <Button
          variant="contained"
          color="secondary"
          onClick={props.deleteTableRows}
        >
          Delete Product(s)
        </Button>
      </Grid>
    : null }
  </React.Fragment>
);

const PurchaseOrderDetails = ({setNewPOProducts, ...props}) => {

  const { displayId, vendorId, poProducts } = props.newPurchaseOrder;

  const [productInput, setProductInput] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [vendorInput, setVendorInput] = useState({firstLetter: "", id: 0, name: "", phone: ""});
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [focusedInput, setFocusedInput] = useState();

  const [openProductModal, setOpenProductModal] = useState(false);
  const [openVendorModal, setOpenVendorModal] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);

  useEffect(() => {
    if(vendorId > 0) {
      const vendorInfo = props.vendors.find(vendor => vendor.id === vendorId);
      const firstLetter = vendorInfo.name[0].toUpperCase();
      setVendorInput({firstLetter, id: vendorInfo.id, name: vendorInfo.name, phone: vendorInfo.phone});
    }

  }, [vendorId, props.vendors]);

  const productsObj = useMemo(() => (
    props.products
      .map(product => {
        const firstLetter = product.name[0].toUpperCase();
        return {
          firstLetter,
          productId: product.id,
          displayId: product.displayId,
          name: product.name,
          category: product.category,
          company: product.company,
          unitCost: product.unitCost
        }
      })
      .filter(product => !poProducts.some(item => item.productId === product.productId))
      .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))

  ), [props.products, poProducts]);

  const vendorsObj = useMemo(() => (
    props.vendors
      .map(vendor => {
        const firstLetter = vendor.name[0].toUpperCase();
        return {firstLetter, id: vendor.id, name: vendor.name, phone: vendor.phone};
      })
      .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))
    
  ), [props.vendors]);

  const columns = [
    { field: 'displayId', headerName: 'Product ID', flex: 1 },
    { field: 'name', headerName: 'Product Name', flex: 1 },
    { field: 'category', headerName: 'Category', flex: 1 },
    { field: 'company', headerName: 'Brand', flex: 1 },
    { field: 'unitCost', headerName: 'Cost', type: 'number', flex: 1 },
    { field: 'totalQuantity', headerName: 'Quantity', type: 'number', flex: 1, editable: true }
  ];

  const savePurchaseOrder = () => {

    const subTotal = poProducts.reduce((prev, curr) => prev + (curr.unitCost * curr.totalQuantity), 0);
    const grandTotal = subTotal + Math.round( subTotal * VAT_UAE );

    const newPurchaseOrder = {
      displayId,
      vendorId,
      startDate,
      endDate,
      total: grandTotal,
      products: poProducts.map(poProduct => ({
        productId: poProduct.productId,
        totalQuantity: poProduct.totalQuantity
      }))
    };
    props.addPurchaseOrder(newPurchaseOrder, props.activeUserId);

    purchaseOrderCleanup();
  };

  const purchaseOrderCleanup = () => {
    setVendorInput({firstLetter: "", id: 0, name: "", phone: ""});
    setSelectionModel([]);
    props.clearNewPO();
    props.setOpen(false);
  };

  const checkProductBarcode = (e) => {
    setBarcodeInput(e.target.value);
    
    if(e.target.value.length > 3) {
      const productInfo = props.products.find(product => product.barcode === e.target.value);
      if(productInfo) {
        const index = poProducts.findIndex(poProduct => poProduct.productId === productInfo.id);
        //if the product is already added to purchaseOrder, increase its quantity.
        if( index > -1 ) {
          setNewPOProducts([
            ...poProducts.slice(0, index),
            {
              ...poProducts[index],
              totalQuantity: poProducts[index].totalQuantity + 1
            },
            ...poProducts.slice(index + 1)
          ]);
        }
        //else add the product to the purchaseOrder
        else {
          //'id' field is added only for the DataGrid object.
          setNewPOProducts([
            ...poProducts,
            {
              id: productInfo.displayId,
              displayId: productInfo.displayId,
              productId: productInfo.id,
              name: productInfo.name,
              category: productInfo.category,
              company: productInfo.company,
              unitCost: productInfo.unitCost,
              totalQuantity: 1
            }
          ]);
        }
        setBarcodeInput("");
      }
    }
  };

  const onDatesChange = ({startDate, endDate}) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleEditCellChangeCommitted = React.useCallback(
    ({ id, field, props }) => {
      const quantityIndex = poProducts.findIndex(poProduct => poProduct.id === id);
      setNewPOProducts([
        ...poProducts.slice(0, quantityIndex),
        {
          ...poProducts[quantityIndex],
          totalQuantity: parseFloat(props.value) || poProducts[quantityIndex].totalQuantity
        },
        ...poProducts.slice(quantityIndex + 1)
      ]);
    }, [poProducts, setNewPOProducts]
  );
    
  const deleteTableRows = () => {
    setNewPOProducts(poProducts.filter(poProduct => selectionModel.indexOf(poProduct.id) === -1));
    setSelectionModel([]);
  };

  const addProductOrVendor = (addProduct) => {
    if(addProduct) setOpenProductModal(true);
    else setOpenVendorModal(true);
  };

  const handleProductVendorClose = (closeProduct) => {
    if(closeProduct) setOpenProductModal(false);
    else setOpenVendorModal(false);
  };

  const createProductDisplayId = () => {
    const length = props.idGen.find(idGen => idGen.info.user === props.activeUserId).body.product;
    let productNum;
    length < 100 ? 
      productNum = new Array(3 + 1 - (length + '').length).join('0') + length
    : productNum = length
    return ("P-" + productNum);
  };

  const createVendorDisplayId = () => {
    const length = props.idGen.find(idGen => idGen.info.user === props.activeUserId).body.vendor;
    let vendorNum;
    length < 100 ? 
      vendorNum = new Array(3 + 1 - (length + '').length).join('0') + length
    : vendorNum = length
    return("VEN-" + vendorNum);
  };

  return (
    <React.Fragment>
      <Card>
        <CardHeader
          subheader="The information can be edited later"
          title="Purchase Order"
        />
        <Divider />
        <CardContent>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              md={6}
              xs={12}
            >
              <Autocomplete
                onChange={(event, newValue) => {
                  if (newValue && newValue.inputValue) {
                    addProductOrVendor(true);
                  } 
                  else {
                    //'id' field is added only for the DataGrid object.
                    setNewPOProducts([
                      ...poProducts,
                      {
                        id: newValue.displayId,
                        displayId: newValue.displayId,
                        productId: newValue.productId,
                        name: newValue.name,
                        category: newValue.category,
                        company: newValue.company,
                        unitCost: newValue.unitCost,
                        totalQuantity: 1
                      }
                    ]);
                    setProductInput("");
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  if (params.inputValue !== '') {
                    filtered.push({
                      inputValue: params.inputValue,
                      name: `Add ${params.inputValue} ...`,
                    });
                  }

                  return filtered;
                }}
                id="free-solo-dialog-demo"
                fullWidth
                options={productsObj}
                groupBy={(product) => product.firstLetter}
                getOptionSelected={(option, value) => option.productId === value.productId}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (<TextField {...params} label="Select Product.." variant="outlined" />)}
                inputValue={productInput}
                onInputChange={(event, newInputValue) => {
                  setProductInput(newInputValue);
                }}
                value={null}
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                helperText="Scan Product Barcode"
                label="Scan Barcode"
                name="barcode"
                onChange={(event) => checkProductBarcode(event)}
                value={barcodeInput}
                variant="outlined"
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <Autocomplete
                onChange={(event, newValue) => {
                  if(newValue && newValue.inputValue) {
                    addProductOrVendor(false);
                  }
                  else {
                    if(newValue) {
                      setVendorInput(newValue);
                      props.setNewPOVendorId(newValue.id);
                    }
                    else {
                      setVendorInput({firstLetter: "", id: 0, name: "", phone: ""});
                      props.setNewPOVendorId(0);
                    }
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  if (params.inputValue !== '') {
                    filtered.push({
                      inputValue: params.inputValue,
                      name: "Add Vendor -",
                      phone: params.inputValue
                    });
                  }

                  return filtered;
                }}
                id="free-solo-dialog-demo"
                fullWidth
                options={vendorsObj}
                groupBy={(vendor) => vendor.firstLetter}
                getOptionSelected={(option, value) => option.id === value.id}
                getOptionLabel={(option) => `${option.name} (${option.phone})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Vendor.."
                    variant="outlined"
                  />
                )}
                value={vendorInput ? ( vendorInput.id ? vendorInput : null ) : null}
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
              style={{textAlign: "center"}}
            >
              <DateRangePicker
                block
                startDateId="123456" // PropTypes.string.isRequired,
                endDateId="567890" // PropTypes.string.isRequired,
                startDate={startDate} // momentPropTypes.momentObj or null,
                endDate={endDate} // momentPropTypes.momentObj or null,
                isOutsideRange={() => false}
                displayFormat="DD/MM/YYYY"
                onDatesChange={onDatesChange} // PropTypes.func.isRequired,
                focusedInput={focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
                onFocusChange={focusedInput => setFocusedInput(focusedInput)} // PropTypes.func.isRequired,
              />
              <FormHelperText>Select The Due Date</FormHelperText>
            </Grid>
          </Grid>
          <Box sx={{
            overflow: 'auto',
            height: 318,
            marginTop: '2%'
          }}>
            <DataGrid
              rows={poProducts}
              columns={columns}
              components={{
                Footer: CustomFooter
              }}
              componentsProps={{
                footer: { selectionModel, deleteTableRows }
              }}
              onEditCellChangeCommitted={handleEditCellChangeCommitted}
              onSelectionModelChange={(newSelectionModel) => {
                setSelectionModel(newSelectionModel.selectionModel);
              }}
              selectionModel={selectionModel}
              checkboxSelection
              disableSelectionOnClick
              localeText={{
                noRowsLabel: 'No Products'
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <h3>Total : {poProducts.reduce((prev, curr) => prev + (curr.unitCost * curr.totalQuantity), 0)} AED</h3>
          </Box>
          <Button
            disabled={ !poProducts[0] || vendorId === 0 || !startDate || !endDate }
            color="primary"
            variant="contained"
            onClick={savePurchaseOrder}
          >
            Save P.O.
          </Button>
          <Button
            color="primary"
            onClick={purchaseOrderCleanup}
          >
            Cancel
          </Button>
        </CardContent>
      </Card>
      { openProductModal ?
      <AddProductModal
        open={openProductModal}
        activeUserId={props.activeUserId}
        productCategories={props.productCategories}
        productCompanies={props.productCompanies}
        displayId={createProductDisplayId()}
        closeModal={handleProductVendorClose}
      /> : null }
      { openVendorModal ?
      <AddVendorModal
        open={openVendorModal}
        activeUserId={props.activeUserId}
        displayId={createVendorDisplayId()}
        closeModal={handleProductVendorClose}
      /> : null }
    </React.Fragment>
  )
};

export default connect (mapStateToProps, {addPurchaseOrder, setNewPOVendorId, setNewPOProducts, setNewPODate, clearNewPO}) (PurchaseOrderDetails);